'use client'

import { useEffect, useState } from 'react'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WorktypeSelect } from '@repo/ui/WorktypeSelect'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

type WorkflowType = { workflowTypeId: string; description: string }

type StepProps = {
  wideMode?: boolean
  handlePrevNext: (delta: number) => void
  updateStepState: (changes: { status?: string; statusText?: string }) => void
  testMode?: boolean
}

export const SelectWorktypeStep = ({ wideMode, handlePrevNext, updateStepState }: StepProps) => {
  const { mappedFormData, setMappedFormData } = useThirdPartyFormsStore() as {
    mappedFormData: { mappingType: 'global' | 'worktype'; workTypes: WorkflowType[] }
    setMappedFormData: (payload: {
      mappedFormData: Partial<{ mappingType: 'global' | 'worktype'; workTypes: WorkflowType[] }>
    }) => void
  }

  const [mappingType, setMappingType] = useState<'global' | 'worktype'>(mappedFormData?.mappingType ?? 'global')
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<WorkflowType[]>(mappedFormData?.workTypes ?? [])

  useEffect(() => {
    setMappedFormData({
      mappedFormData: {
        mappingType,
        workTypes: mappingType === 'worktype' ? selectedWorkTypes : []
      }
    })
  }, [mappingType, selectedWorkTypes, setMappedFormData])

  const continueDisabled = mappingType === 'worktype' && selectedWorkTypes.length === 0

  const handleContinue = () => {
    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  return (
    <>
      <WizardPanel data-testid="SelectWorktypeStep">
        <MUIStack gap={3} sx={{ width: '100%' }}>
          <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
            Is this mapping rule global, or is it worktype-specific
          </MUITypography>

          <MUIRadioGroup
            value={mappingType}
            onChange={(e) => setMappingType(e.target.value as 'global' | 'worktype')}
          >
            <MUIFormControlLabel value="global" control={<MUIRadio />} label="Global" />
            <MUIFormControlLabel value="worktype" control={<MUIRadio />} label="One or more worktypes" />
          </MUIRadioGroup>

          {mappingType === 'worktype' && (
            <MUIStack sx={{ ml: 7, width: 320 }}>
              <WorktypeSelect
                multiple
                value={selectedWorkTypes}
                onChange={(value: WorkflowType[] | WorkflowType | null) => {
                  const next = Array.isArray(value) ? value : value ? [value] : []
                  setSelectedWorkTypes(next)
                }}
                size="medium"
              />
            </MUIStack>
          )}
        </MUIStack>
      </WizardPanel>

      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleContinue}
        handleClickBack={() => handlePrevNext(-1)}
        continueButtonText="Select form"
        backButtonVisible={false}
        continueButtonDisabled={continueDisabled}
      />
    </>
  )
}
